// Terms and Conditions of Analytical Platform (EN)
// Generated from the source Word document - keep the wording verbatim.
import type { LegalDocument } from "./types";

const termsEn: LegalDocument = {
  "title": "Terms and Conditions of Analytical Platform",
  "effectiveFrom": "Effective from 18 September 2026",
  "sections": [
    {
      "heading": "1. Introductory Provisions",
      "blocks": [
        {
          "kind": "clause",
          "number": "1.1.",
          "text": "These Terms and Conditions (the “Terms”) govern the use of the Analytical Platform technology platform, the individual applications, products, tools and functionalities made available through it, and related services (collectively, the “Service”)."
        },
        {
          "kind": "clause",
          "number": "1.2.",
          "text": "Analytical Platform is operated by CCFR, an association of CCF RESEARCH, a.s. and CCFR SOFTWARE, s.r.o. (the “Provider”)."
        },
        {
          "kind": "clause",
          "number": "1.3.",
          "text": "Analytical Platform is a technology environment through which the Provider makes individual analytical applications, products, tools and related functionalities available to Users."
        },
        {
          "kind": "clause",
          "number": "1.4.",
          "text": "Individual applications or products may also be presented on separate websites. Their functionalities may be provided through the shared Analytical Platform technology environment and a shared user account."
        },
        {
          "kind": "clause",
          "number": "1.5.",
          "text": "The scope of available applications, products and functionalities may vary depending on the specific product, selected subscription plan, technical availability or other conditions applicable to the relevant functionality."
        },
        {
          "kind": "clause",
          "number": "1.6.",
          "text": "Specific terms and conditions or legal notices may be published for a particular application, product or functionality. Where such specific terms are published, they apply together with these Terms. In the event of a conflict, the specific terms prevail in relation to the relevant application, product or functionality."
        },
        {
          "kind": "clause",
          "number": "1.7.",
          "text": "A “User” means any natural or legal person using the Service."
        },
        {
          "kind": "clause",
          "number": "1.8.",
          "text": "By using the Service, the User confirms that they have read and agree to these Terms."
        }
      ]
    },
    {
      "heading": "2. Nature of the Service",
      "blocks": [
        {
          "kind": "clause",
          "number": "2.1.",
          "text": "Analytical Platform is a technology platform containing analytical applications, products and tools intended primarily for working with financial and investment data, analysing investment instruments and markets, creating models, working with investment characteristics, analysing and constructing portfolios, and other related analytical activities."
        },
        {
          "kind": "clause",
          "number": "2.2.",
          "text": "Individual applications and functionalities may provide the User with data, information, calculations, models, statistics, analyses, simulations and other analytical outputs."
        },
        {
          "kind": "clause",
          "number": "2.3.",
          "text": "The specific functions available to a User depend on the application or product used and, where applicable, the selected subscription plan. Not every functionality described in these Terms is necessarily available in every application."
        },
        {
          "kind": "clause",
          "number": "2.4.",
          "text": "Outputs of the Service are intended to support the User's own decision-making."
        },
        {
          "kind": "clause",
          "number": "2.5.",
          "text": "The Provider does not decide on behalf of the User which investment instruments the User should invest in, buy, sell or hold, or what portfolio composition is suitable for the User."
        },
        {
          "kind": "clause",
          "number": "2.6.",
          "text": "All investment decisions are made independently by the User and at the User's own responsibility."
        }
      ]
    },
    {
      "heading": "3. Investment Advice and Suitability Assessment",
      "blocks": [
        {
          "kind": "clause",
          "number": "3.1.",
          "text": "The Provider does not provide investment advice or personalised investment recommendations through the Service."
        },
        {
          "kind": "clause",
          "number": "3.2.",
          "text": "For the purpose of assessing the suitability of an investment, investment instrument, portfolio or potential rebalancing, the Provider does not collect or assess information concerning, in particular:"
        },
        {
          "kind": "bullets",
          "items": [
            "the User's investment knowledge;",
            "the User's investment experience;",
            "the User's financial situation and financial resources;",
            "the User's ability to bear losses;",
            "the User's investment objectives;",
            "the User's investment horizon; or",
            "the User's risk tolerance."
          ]
        },
        {
          "kind": "clause",
          "number": "3.3.",
          "text": "The Provider does not assess whether a particular investment instrument, portfolio, portfolio composition or potential change is suitable for a particular User."
        },
        {
          "kind": "clause",
          "number": "3.4.",
          "text": "Where a functionality enables the User to set parameters or criteria used for analysis, portfolio construction or portfolio adjustment, such parameters and criteria are selected independently by the User. The Provider does not determine them with regard to the User's personal, financial or other individual circumstances."
        },
        {
          "kind": "clause",
          "number": "3.5.",
          "text": "Outputs based on parameters or criteria selected by the User are generated on the basis of those parameters and the predefined rules of the relevant functionality."
        },
        {
          "kind": "clause",
          "number": "3.6.",
          "text": "The Provider does not verify whether information about the current composition of a portfolio or other data entered or made available by the User is true, correct, complete or up to date."
        }
      ]
    },
    {
      "heading": "4. Investment Research and Investment Recommendations",
      "blocks": [
        {
          "kind": "clause",
          "number": "4.1.",
          "text": "Information, investment characteristics, scores, rankings, models, calculations, statistics, historical analyses, simulations and other outputs of the Service constitute analytical outputs of software tools."
        },
        {
          "kind": "clause",
          "number": "4.2.",
          "text": "Such outputs do not constitute a recommendation to the User to buy, sell or hold a particular investment instrument or an investment strategy tailored to a particular User."
        },
        {
          "kind": "clause",
          "number": "4.3.",
          "text": "The Provider does not provide investment research or investment recommendations through the Service within the meaning of applicable law."
        },
        {
          "kind": "clause",
          "number": "4.4.",
          "text": "No output of the Service may be regarded as a promise or guarantee of the future performance of an investment instrument, portfolio or market."
        }
      ]
    },
    {
      "heading": "5. Investment Risks and Past Performance",
      "blocks": [
        {
          "kind": "clause",
          "number": "5.1.",
          "text": "Investing in investment instruments involves risk. The value of investments may rise or fall and the return on an investment is not guaranteed."
        },
        {
          "kind": "clause",
          "number": "5.2.",
          "text": "The User may lose part or all of the amount invested."
        },
        {
          "kind": "clause",
          "number": "5.3.",
          "text": "Past returns and historical performance are neither a guarantee nor a reliable indicator of future returns."
        },
        {
          "kind": "clause",
          "number": "5.4.",
          "text": "Results of backtests, simulations, optimisations, models and other calculations based on historical data are hypothetical. Actual future results may differ materially from historical or modelled outputs."
        },
        {
          "kind": "clause",
          "number": "5.5.",
          "text": "Historical simulations and other calculations may not reflect all circumstances of real trading, including liquidity, spreads, slippage, transaction costs, fees, taxes, currency conversions or changes in market conditions, unless expressly stated otherwise for a particular output."
        }
      ]
    },
    {
      "heading": "6. Data and Nature of Service Outputs",
      "blocks": [
        {
          "kind": "clause",
          "number": "6.1.",
          "text": "The Service uses data and information from various external sources, including providers of market and financial data, exchanges, publicly available sources and other third parties (“Data”)."
        },
        {
          "kind": "clause",
          "number": "6.2.",
          "text": "The Provider does not have full control over the creation, correctness, completeness, accuracy, timeliness or availability of the Data and does not independently verify its accuracy unless expressly stated otherwise."
        },
        {
          "kind": "clause",
          "number": "6.3.",
          "text": "Data may contain errors, inaccuracies, omissions, duplicates or delays. Errors may relate in particular to prices, currencies, identification of investment instruments, corporate actions, fundamental data, historical data or other information."
        },
        {
          "kind": "clause",
          "number": "6.4.",
          "text": "Outputs of the Service are automated results of software systems operating on available Data, configured algorithms and models and, where applicable, parameters selected by the User."
        },
        {
          "kind": "clause",
          "number": "6.5.",
          "text": "Incorrect, incomplete, inaccurate or delayed Data may affect outputs of the Service, including analyses, investment characteristics, scores, calculations, models, simulations, portfolio composition or the result of potential rebalancing."
        },
        {
          "kind": "clause",
          "number": "6.6.",
          "text": "The Provider does not guarantee the correctness, completeness, accuracy, timeliness or uninterrupted availability of the Data."
        },
        {
          "kind": "clause",
          "number": "6.7.",
          "text": "Data, technical processing, algorithms, models, automated calculations or other technological components of the Service may contain errors, inaccuracies or deviations in individual cases."
        },
        {
          "kind": "clause",
          "number": "6.8.",
          "text": "The User acknowledges that outputs of the Service are analytical and informational in nature and constitute only one possible input into the User's own decision-making."
        },
        {
          "kind": "clause",
          "number": "6.9.",
          "text": "Before making an investment decision or transaction, the User is required to verify information material to that decision."
        },
        {
          "kind": "clause",
          "number": "6.10.",
          "text": "If the User suffers loss as a result of incorrect Data supplied by a third party, the Provider will, upon request and to the extent that it has the relevant information available and is entitled to disclose it, provide information necessary to identify the relevant Data provider and potentially assert a claim directly against that provider."
        }
      ]
    },
    {
      "heading": "7. Portfolio and Rebalancing Functionalities",
      "blocks": [
        {
          "kind": "clause",
          "number": "7.1.",
          "text": "Certain applications or functionalities of the Service may allow the User to construct, analyse, modify or rebalance an investment portfolio according to parameters and criteria selected by the User."
        },
        {
          "kind": "clause",
          "number": "7.2.",
          "text": "Where the Service allows automated calculation of rebalancing, the resulting rebalancing is generated according to parameters and criteria selected by the User and the predefined rules of the relevant functionality."
        },
        {
          "kind": "clause",
          "number": "7.3.",
          "text": "In such rebalancing, the Provider has no discretion as to how a particular User's assets should be invested and does not assess whether the resulting rebalancing is suitable for the User."
        },
        {
          "kind": "clause",
          "number": "7.4.",
          "text": "The resulting rebalancing is an output of an analytical tool. It is solely for the User to decide whether to actually change the portfolio on the basis of that output."
        },
        {
          "kind": "clause",
          "number": "7.5.",
          "text": "The Provider does not manage the User's assets containing investment instruments on a discretionary basis under a contractual arrangement."
        },
        {
          "kind": "clause",
          "number": "7.6.",
          "text": "The result of rebalancing may be affected in particular by Data, parameters selected by the User, the current portfolio composition, technical processing and the rules of the relevant application or functionality."
        }
      ]
    },
    {
      "heading": "8. Broker Connectivity and Intermediation",
      "blocks": [
        {
          "kind": "clause",
          "number": "8.1.",
          "text": "Certain applications or functionalities of the Service may enable technical connectivity to the User's investment account maintained with a supported investment services provider (a “Broker”)."
        },
        {
          "kind": "clause",
          "number": "8.2.",
          "text": "To use such functionality, the User must independently maintain an account with the relevant Broker and have a contractual relationship with that Broker."
        },
        {
          "kind": "clause",
          "number": "8.3.",
          "text": "The Provider is not a party to the contractual relationship between the User and the Broker and does not act as representative of either party."
        },
        {
          "kind": "clause",
          "number": "8.4.",
          "text": "The Provider does not intermediate transactions in investment instruments or the provision of investment services. In particular, it does not, on behalf of the User or Broker, arrange transactions or investment services, submit proposals for their conclusion, carry out preparatory work aimed at their conclusion, or provide recommendations leading to their conclusion."
        },
        {
          "kind": "clause",
          "number": "8.5.",
          "text": "The Provider is not remunerated by a Broker because the User holds or rebalances a portfolio with that Broker, unless expressly stated otherwise for a particular product or cooperation."
        },
        {
          "kind": "clause",
          "number": "8.6.",
          "text": "Technical connectivity may only be available for Brokers for which such connectivity is technically possible. The range of supported Brokers may change over time."
        }
      ]
    },
    {
      "heading": "9. Receipt and Transmission of Orders",
      "blocks": [
        {
          "kind": "clause",
          "number": "9.1.",
          "text": "The Provider does not receive orders concerning investment instruments from the User for transmission to third parties and does not provide an order receipt and transmission service."
        },
        {
          "kind": "clause",
          "number": "9.2.",
          "text": "Where a functionality enables the User to follow an analytical output with a potential transaction, it is solely for the User to decide whether to carry out such transaction."
        },
        {
          "kind": "clause",
          "number": "9.3.",
          "text": "Where the Service integrates an interface enabling communication with a Broker, communication relating to a potential investment order takes place through the technical connection between the User and the Broker. The Provider does not decide whether an order is placed and does not alter its content."
        },
        {
          "kind": "clause",
          "number": "9.4.",
          "text": "Before confirming a transaction, the User must verify in particular the identification of the investment instrument, transaction direction, quantity and other material transaction parameters displayed by the Broker."
        },
        {
          "kind": "clause",
          "number": "9.5.",
          "text": "After a transaction, the User must also verify its actual execution and consistency with the order submitted to the Broker."
        },
        {
          "kind": "clause",
          "number": "9.6.",
          "text": "The Provider does not guarantee that the Broker will accept, execute or settle an order."
        }
      ]
    },
    {
      "heading": "10. Risks of Broker Connectivity and Transaction Execution",
      "blocks": [
        {
          "kind": "clause",
          "number": "10.1.",
          "text": "Where the User uses functionality connected to a Broker, the price, liquidity, availability or other characteristics of an investment instrument may change between the creation of an analytical output or rebalancing calculation and actual execution of a transaction."
        },
        {
          "kind": "clause",
          "number": "10.2.",
          "text": "The price actually achieved by the Broker may differ from the price displayed or used by the Service."
        },
        {
          "kind": "clause",
          "number": "10.3.",
          "text": "An order may be executed only partially, with delay, at a different price, or not at all."
        },
        {
          "kind": "clause",
          "number": "10.4.",
          "text": "The Provider is not responsible for any act or omission of the Broker, including acceptance, rejection, execution or settlement of an order, the achieved price, liquidity, technical availability of the Broker's services or accuracy of records maintained by the Broker."
        },
        {
          "kind": "clause",
          "number": "10.5.",
          "text": "The Provider cannot guarantee uninterrupted availability, proper operation or compatibility of a Broker API or other technical interface."
        },
        {
          "kind": "clause",
          "number": "10.6.",
          "text": "The User acknowledges that technical connectivity between the Service and a Broker may be affected by circumstances beyond the Provider's control, including outages, delays, API changes, restrictions imposed by the Broker or communication network failures."
        }
      ]
    },
    {
      "heading": "11. Technical Errors and Availability of the Service",
      "blocks": [
        {
          "kind": "clause",
          "number": "11.1.",
          "text": "The Provider endeavours to ensure proper and secure operation of the Service but cannot guarantee uninterrupted availability or absolute error-free operation."
        },
        {
          "kind": "clause",
          "number": "11.2.",
          "text": "The Service, individual applications or functionalities may be temporarily unavailable due in particular to maintenance, updates, technical failures, infrastructure outages, internet connectivity, APIs or third-party services."
        },
        {
          "kind": "clause",
          "number": "11.3.",
          "text": "Outputs of the Service may be affected by software errors, incorrect or incomplete input Data, technical failures, calculation, algorithm or model errors, or incorrect communication with external systems."
        },
        {
          "kind": "clause",
          "number": "11.4.",
          "text": "The User must not rely on the Service as the sole source of information for an investment decision and is responsible for checking the final parameters before carrying out a transaction."
        },
        {
          "kind": "clause",
          "number": "11.5.",
          "text": "The Provider may perform scheduled or unscheduled maintenance, updates, repairs and other technical interventions which may temporarily limit the availability of the Service or any of its functionalities."
        }
      ]
    },
    {
      "heading": "12. Limitation of Liability",
      "blocks": [
        {
          "kind": "clause",
          "number": "12.1.",
          "text": "The User uses the Service and its analytical outputs at the User's own responsibility."
        },
        {
          "kind": "clause",
          "number": "12.2.",
          "text": "The Provider is not responsible for the User's investment decisions or the outcome of any investment made using information or functionalities available through the Service."
        },
        {
          "kind": "clause",
          "number": "12.3.",
          "text": "To the maximum extent permitted by applicable law, the Provider shall not be liable for loss or damage arising from:"
        },
        {
          "kind": "bullets",
          "items": [
            "incorrect, incomplete, inaccurate, delayed or missing Data;",
            "incorrect or incomplete data entered or made available by the User;",
            "outputs based on incorrect or incomplete input data;",
            "errors in calculations, algorithms, models or software;",
            "errors in analytical outputs, simulations, optimisations or other automated calculations;",
            "errors in the calculation or construction of potential rebalancing;",
            "outage, unavailability or malfunction of the Service or any application or functionality;",
            "outage, error or change to a Broker API or another third party;",
            "differences between a price used by the Service and the actual execution price;",
            "non-execution, delayed execution or partial execution of a transaction by a Broker;",
            "acts or omissions of a Broker, Data provider or other third party; or",
            "an investment decision made by the User using outputs of the Service."
          ]
        },
        {
          "kind": "clause",
          "number": "12.4.",
          "text": "To the maximum extent permitted by applicable law, the Provider shall not be liable for lost profits, loss of investment opportunity or other indirect or consequential loss."
        },
        {
          "kind": "clause",
          "number": "12.5.",
          "text": "The Provider gives no guarantee that use of the Service or any application or functionality will result in a particular return, reduction in risk, improved portfolio performance or any other investment outcome."
        },
        {
          "kind": "clause",
          "number": "12.6.",
          "text": "Nothing in these Terms excludes or limits liability where such exclusion or limitation is not permitted by applicable law."
        }
      ]
    },
    {
      "heading": "13. Registration and User Account",
      "blocks": [
        {
          "kind": "clause",
          "number": "13.1.",
          "text": "Some or all functionalities of the Service may be available only to registered Users."
        },
        {
          "kind": "clause",
          "number": "13.2.",
          "text": "A user account may provide access to multiple applications, products or functionalities available through Analytical Platform."
        },
        {
          "kind": "clause",
          "number": "13.3.",
          "text": "The User is responsible for the correctness and currency of information provided during registration and for securing the user account."
        },
        {
          "kind": "clause",
          "number": "13.4.",
          "text": "The User must protect login credentials and must not allow an unauthorised person to use the account."
        },
        {
          "kind": "clause",
          "number": "13.5.",
          "text": "The User must notify the Provider without undue delay of any suspected unauthorised use of the account."
        },
        {
          "kind": "clause",
          "number": "13.6.",
          "text": "For security reasons, the Provider may require additional verification of the User or a change of access credentials."
        }
      ]
    },
    {
      "heading": "14. Pricing and Subscription",
      "blocks": [
        {
          "kind": "clause",
          "number": "14.1.",
          "text": "Certain applications, products or functionalities may be provided free of charge, while others may only be available under a paid subscription."
        },
        {
          "kind": "clause",
          "number": "14.2.",
          "text": "The price of the Service, the scope of individual plans and any applicable restrictions are stated on the relevant website or in the application."
        },
        {
          "kind": "clause",
          "number": "14.3.",
          "text": "Before ordering a paid Service, the User is informed of its price, subscription period and any automatic renewal."
        },
        {
          "kind": "clause",
          "number": "14.4.",
          "text": "Details of payment method, subscription duration, renewal and termination options are provided before the User orders a paid Service."
        },
        {
          "kind": "clause",
          "number": "14.5.",
          "text": "Ending or changing the use of one application or product does not necessarily terminate the user account or access to other applications and products unless the conditions of the relevant subscription provide otherwise."
        }
      ]
    },
    {
      "heading": "15. Intellectual Property and Use of the Service",
      "blocks": [
        {
          "kind": "clause",
          "number": "15.1.",
          "text": "Analytical Platform, individual applications and products, their software, algorithms, models, database structure, methodologies, texts, graphics and other content are protected by intellectual property rights of the Provider or third parties."
        },
        {
          "kind": "clause",
          "number": "15.2.",
          "text": "The User receives only a limited, non-exclusive and non-transferable right to use the Service for its intended purposes and in accordance with these Terms and any specific terms applicable to a particular application or product."
        },
        {
          "kind": "clause",
          "number": "15.3.",
          "text": "Without the Provider's prior consent, the User may not in particular:"
        },
        {
          "kind": "bullets",
          "items": [
            "systematically copy, download or redistribute content or Data from the Service;",
            "commercially exploit content or Data from the Service beyond the scope expressly permitted by the Provider;",
            "scrape or otherwise automatically extract content or Data;",
            "circumvent technical or access restrictions;",
            "attempt to obtain source code of the Service;",
            "reverse engineer, decompile or perform similar actions unless expressly permitted by applicable law; or",
            "make the user account available to unauthorised third parties."
          ]
        },
        {
          "kind": "clause",
          "number": "15.4.",
          "text": "Data supplied by third parties may be subject to separate licensing restrictions imposed by the relevant providers."
        },
        {
          "kind": "clause",
          "number": "15.5.",
          "text": "The User may not use the Service in a manner that may harm the Provider, other Users, Data providers, Brokers or other third parties or compromise the security or operation of the Service."
        }
      ]
    },
    {
      "heading": "16. Changes to and Termination of the Service",
      "blocks": [
        {
          "kind": "clause",
          "number": "16.1.",
          "text": "The Provider may continuously update, change and develop Analytical Platform and its individual applications, products and functionalities."
        },
        {
          "kind": "clause",
          "number": "16.2.",
          "text": "The Provider may in particular add new applications or functionalities, modify existing functionalities, or restrict or discontinue individual applications or functionalities."
        },
        {
          "kind": "clause",
          "number": "16.3.",
          "text": "Availability of certain functions may be changed, restricted or discontinued in particular where cooperation with a Data provider, Broker or other technology supplier changes."
        },
        {
          "kind": "clause",
          "number": "16.4.",
          "text": "The Provider may restrict or terminate the User's access to the Service or any part of it if the User uses the Service in breach of these Terms, specific terms applicable to a product, or applicable law."
        },
        {
          "kind": "clause",
          "number": "16.5.",
          "text": "The Provider may also restrict access where necessary for security reasons or to protect the Service, other Users or third parties."
        }
      ]
    },
    {
      "heading": "17. Personal Data Protection",
      "blocks": [
        {
          "kind": "clause",
          "number": "17.1.",
          "text": "Processing of the User's personal data is governed by the Provider's separate Privacy Policy."
        },
        {
          "kind": "clause",
          "number": "17.2.",
          "text": "Depending on their functionality, individual applications or products may process different categories of data. Details are provided in the relevant data protection information where published for the particular application or product."
        }
      ]
    },
    {
      "heading": "18. Governing Law and Consumers",
      "blocks": [
        {
          "kind": "clause",
          "number": "18.1.",
          "text": "These Terms and legal relationships arising in connection with use of the Service are governed by the laws of the Czech Republic."
        },
        {
          "kind": "clause",
          "number": "18.2.",
          "text": "If the User is a consumer, these Terms do not affect rights arising under mandatory consumer protection laws that cannot be contractually excluded or limited."
        },
        {
          "kind": "clause",
          "number": "18.3.",
          "text": "If the User is a consumer, the User will be provided, in connection with ordering a paid Service or otherwise entering into a contractual relationship, with the information and rights required by applicable law for the provision of digital content or digital services."
        }
      ]
    },
    {
      "heading": "19. Final Provisions",
      "blocks": [
        {
          "kind": "clause",
          "number": "19.1.",
          "text": "The Provider may amend these Terms to a reasonable extent, in particular in response to changes to the Service, individual applications or products, applicable law, technologies used or the business model."
        },
        {
          "kind": "clause",
          "number": "19.2.",
          "text": "The User will be informed of material changes to these Terms in an appropriate manner."
        },
        {
          "kind": "clause",
          "number": "19.3.",
          "text": "The current version of these Terms is available through Analytical Platform or on the Provider's website."
        },
        {
          "kind": "clause",
          "number": "19.4.",
          "text": "If any provision of these Terms is invalid, ineffective or unenforceable, this does not affect the validity or effectiveness of the remaining provisions."
        },
        {
          "kind": "clause",
          "number": "19.5.",
          "text": "Where specific terms are published for a particular application, product or functionality, they apply together with these Terms and, in the event of a conflict, prevail in relation to that application, product or functionality."
        },
        {
          "kind": "clause",
          "number": "19.6.",
          "text": "The User may contact the Provider regarding the Service at:"
        },
        {
          "kind": "note",
          "lines": [
            "CCFR",
            "Email: app@analyticalplatform.com",
            "Phone: +420 530 338 808"
          ]
        }
      ]
    }
  ]
};

export default termsEn;
